import { useCallback, useState } from 'react';
import { FileUploadState, UploadResult } from './types';

interface UseFileUploadStateProps {
    onChange?: (state: FileUploadState) => void;
}

export function useFileUploadState({ onChange }: UseFileUploadStateProps) {
    const [uploadedFiles, setUploadedFiles] = useState<UploadResult[]>([]);
    const [error, setError] = useState<string | null>(null);

    const notifyChange = useCallback(
        (files: UploadResult[], err: string | null) => {
            onChange?.({ files, error: err });
        },
        [onChange]
    );

    const handleUploadSuccess = useCallback(
        (result: UploadResult[]) => {
            setUploadedFiles((prev) => {
                const updatedFiles = [...prev, ...result];
                setError(null);
                notifyChange(updatedFiles, null);
                return updatedFiles;
            });
        },
        [notifyChange]
    );

    const handleUploadError = useCallback(
        (err: Error) => {
            const errorMessage = err.message;
            setError(errorMessage);
            notifyChange(uploadedFiles, errorMessage);
        },
        [notifyChange, uploadedFiles]
    );

    const handleRemove = useCallback(
        (fileUrl: string) => {
            setUploadedFiles((prev) => {
                const updatedFiles = prev.filter((f) => f.url !== fileUrl);
                setError(null);
                notifyChange(updatedFiles, null);
                return updatedFiles;
            });
        },
        [notifyChange]
    );

    return {
        uploadedFiles,
        error,
        handleUploadSuccess,
        handleUploadError,
        handleRemove,
    };
}

